import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  TextField,
  Alert,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Box,
} from '@mui/material';
import { api } from '../contexts/ApiContext';
import {
  FIT_TABLE_SX,
  FIT_TABLE_CONTAINER_SX,
  FitColgroup,
  fitTruncateSx,
} from '../utils/fitTable';

export type TxLinkDocumentType = 'receipt' | 'sales_invoice' | 'purchase_invoice';

interface LibraryDocument {
  uid: string;
  file_name: string;
  created_at: string;
  document_type: TxLinkDocumentType;
}

interface TxLinkExistingDocumentDialogProps {
  open: boolean;
  transactionId: number | null;
  onClose: () => void;
  onLinked?: (info: { transactionId: number; documentType: TxLinkDocumentType; uid: string }) => void;
}

const typeLabel = (type: TxLinkDocumentType): string => {
  if (type === 'sales_invoice') return 'Sales invoice';
  if (type === 'purchase_invoice') return 'Purchase invoice';
  return 'Receipt';
};

/**
 * Pick an already-uploaded receipt or invoice and link it to a ledger transaction.
 */
export const TxLinkExistingDocumentDialog: React.FC<TxLinkExistingDocumentDialogProps> = ({
  open,
  transactionId,
  onClose,
  onLinked,
}) => {
  const [documents, setDocuments] = useState<LibraryDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [linkingUid, setLinkingUid] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError('');
      setQuery('');
      try {
        const [receiptsRes, salesRes, purchaseRes] = await Promise.all([
          api.get('/api/receipts'),
          api.get('/api/sales-invoices'),
          api.get('/api/purchase-invoices'),
        ]);
        if (cancelled) return;
        const receipts = Array.isArray(receiptsRes.data) ? receiptsRes.data : [];
        const sales = Array.isArray(salesRes.data) ? salesRes.data : [];
        const purchases = Array.isArray(purchaseRes.data) ? purchaseRes.data : [];
        const rows: LibraryDocument[] = [
          ...receipts.map((row: { uid: string; file_name: string; created_at: string }) => ({
            uid: row.uid,
            file_name: row.file_name,
            created_at: row.created_at,
            document_type: 'receipt' as const,
          })),
          ...sales.map((row: { uid: string; file_name: string; created_at: string }) => ({
            uid: row.uid,
            file_name: row.file_name,
            created_at: row.created_at,
            document_type: 'sales_invoice' as const,
          })),
          ...purchases.map((row: { uid: string; file_name: string; created_at: string }) => ({
            uid: row.uid,
            file_name: row.file_name,
            created_at: row.created_at,
            document_type: 'purchase_invoice' as const,
          })),
        ].sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''));
        setDocuments(rows);
      } catch (err: any) {
        if (!cancelled) {
          setError(err?.response?.data?.error || err?.message || 'Failed to load documents');
          setDocuments([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return documents;
    return documents.filter((row) => (
      row.file_name.toLowerCase().includes(needle)
      || typeLabel(row.document_type).toLowerCase().includes(needle)
      || row.uid.toLowerCase().includes(needle)
    ));
  }, [documents, query]);

  const handleLink = async (doc: LibraryDocument) => {
    if (!transactionId) {
      setError('No transaction selected.');
      return;
    }
    try {
      setLinkingUid(doc.uid);
      setError('');
      await api.post('/api/transaction-documents/link', {
        transaction_id: transactionId,
        document_type: doc.document_type,
        document_uid: doc.uid,
      });
      onLinked?.({ transactionId, documentType: doc.document_type, uid: doc.uid });
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to link document');
    } finally {
      setLinkingUid(null);
    }
  };

  return (
    <Dialog open={open} onClose={() => !linkingUid && onClose()} maxWidth="md" fullWidth>
      <DialogTitle>
        Link document{transactionId ? ` to TX ${transactionId}` : ''}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Choose a receipt or invoice already in the library. To upload a new file for this
            transaction, use the upload icon on the row.
          </Typography>
          {error ? <Alert severity="error">{error}</Alert> : null}
          <TextField
            size="small"
            label="Filter"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="File name or type"
            fullWidth
          />
          {loading ? (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <TableContainer component={Paper} sx={{ ...FIT_TABLE_CONTAINER_SX, maxHeight: '50vh' }}>
              <Table size="small" stickyHeader sx={FIT_TABLE_SX}>
                <FitColgroup
                  cols={[
                    { key: 'type', pct: 22 },
                    { key: 'file' },
                    { key: 'date', pct: 22 },
                    { key: 'action', pct: 16 },
                  ]}
                />
                <TableHead>
                  <TableRow>
                    <TableCell>Type</TableCell>
                    <TableCell>File</TableCell>
                    <TableCell>Uploaded</TableCell>
                    <TableCell align="right">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        {documents.length === 0
                          ? 'No documents in the library yet. Upload one from Receipts & Invoices, or use the upload icon on this row.'
                          : 'No documents match this filter.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filtered.map((doc) => (
                      <TableRow key={`${doc.document_type}-${doc.uid}`} hover>
                        <TableCell>{typeLabel(doc.document_type)}</TableCell>
                        <TableCell>
                          <Box component="span" sx={fitTruncateSx} title={doc.file_name}>
                            {doc.file_name}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {doc.created_at ? new Date(doc.created_at).toLocaleDateString('en-IE') : '—'}
                        </TableCell>
                        <TableCell align="right" className="fit-table-no-clip">
                          <Button
                            size="small"
                            variant="contained"
                            disabled={linkingUid != null || !transactionId}
                            onClick={() => void handleLink(doc)}
                          >
                            {linkingUid === doc.uid ? 'Linking…' : 'Link'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={linkingUid != null}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default TxLinkExistingDocumentDialog;
